import styled from "styled-components";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import {
    BiUpvote,
    BiDownvote,
    BiMessageRounded,
    BiShareAlt,
    BiFlag,
    BiDotsVerticalRounded,
    BiEdit,
    BiTrashAlt,
    BiPin,
} from "react-icons/bi";
import { GoArrowUp, GoArrowDown } from "react-icons/go";
import { selectUser } from "../../features/userSlice";
import { uploadToCloudinary } from "../../utils/cloudinary";
import { subscribeVerifiedUsers, getBadge } from "../../utils/verified";
import { VerifiedBadge } from "../VerifiedBadge/VerifiedBadge";
import {
    subscribeCommunity,
    subscribeCommunityPosts,
    toggleMembership,
    requestJoin,
    leaveCommunity,
    decideRequest,
    createCommunityPost,
    voteCommunityPost,
    subscribePostComments,
    addPostComment,
    editCommunityPost,
    deleteCommunityPost,
    togglePinPost,
    editCommunityComment,
    deleteCommunityComment,
    subscribeCommunityMembers,
    removeMember,
    banMember,
    unbanMember,
    transferOwnership,
} from "../../utils/communities";

dayjs.extend(relativeTime);

const FALLBACK_AVATAR =
    "https://cdn.pixabay.com/photo/2016/08/08/09/17/avatar-1577909_960_720.png";

const SORTS = ["Hot", "New", "Top", "Most Commented"];

export const CommunityPage = () => {
    const { slug } = useParams();
    const user = useSelector(selectUser);
    const [community, setCommunity] = useState(null);
    const [posts, setPosts] = useState([]);
    const [members, setMembers] = useState([]);
    const [openMenu, setOpenMenu] = useState(null);
    const [verifiedMap, setVerifiedMap] = useState({});

    useEffect(() => subscribeCommunity(slug, setCommunity), [slug]);
    useEffect(() => {
        const unsub = subscribeVerifiedUsers(setVerifiedMap);
        return unsub;
    }, []);
    useEffect(() => {
        if (!community) return;
        return subscribeCommunityPosts(community.id, setPosts);
    }, [community]);
    useEffect(() => {
        if (!community) return;
        return subscribeCommunityMembers(community.id, setMembers);
    }, [community]);

    if (community === null)
        return <Centered>Loading community...</Centered>;
    if (community === undefined)
        return <Centered>Community not found.</Centered>;

    const uid = user?.uid;
    const membersArr = community.members || [];
    const requestsAcc = community.joinRequests || [];
    const admins = community.admins || [];
    const bannedUids = community.bannedUids || [];
    const isMember = uid && membersArr.includes(uid);
    const isPending = uid && requestsAcc.some((r) => r.uid === uid);
    const isAdmin =
        uid && (uid === community.founderUid || admins.includes(uid));
    const isOwner = uid && uid === community.founderUid;
    const isBanned = uid && bannedUids.includes(uid);

    const handleJoinClick = async () => {
        if (!uid || isBanned) return;
        if (isMember || isPending) await leaveCommunity(community.id, user);
        else if (community.visibility === "public") await toggleMembership(community.id, user);
        else if (community.visibility === "restricted") await requestJoin(community.id, user);
    };

    const membershipLabel = isBanned
        ? "Banned"
        : isPending
        ? "Pending"
        : isMember
        ? "Leave"
        : community.visibility === "restricted"
        ? "Request to Join"
        : "Join";

    return (
        <Page>
            <Header banner={community.banner}>
                <div className="banner" />
                <div className="head-inner">
                    <AvatarC avatar={community.avatar} name={community.name} />
                    <div className="info">
                        <h1>{community.name}</h1>
                        <div className="meta">
                            <span className="cat">{community.category}</span>
                            <span className="dot">•</span>
                            <span>{membersArr.length} Fellows</span>
                            <span className="dot">•</span>
                            <span>
                                Owned by {community.founderName}
                                <VerifiedBadge type="purple" size={15} />
                            </span>
                        </div>
                        <div className="actions">
                            <button className="join" onClick={handleJoinClick} disabled={isBanned}>
                                {membershipLabel}
                            </button>
                            <button className="icon-btn" title="Share" onClick={() => navigator.clipboard?.writeText(window.location.href)}>
                                <BiShareAlt /> Share
                            </button>
                            <button className="icon-btn" title="Report"><BiFlag /> Report</button>
                        </div>
                    </div>
                </div>
            </Header>

            <Body>
                <MainCol>
                    {isBanned && (
                        <BannedCard>
                            You are banned from this community. You cannot post or comment here.
                        </BannedCard>
                    )}

                    {/* Admin join requests */}
                    {isAdmin && requestsAcc.length > 0 && (
                        <RequestsCard>
                            <h3>Join Requests ({requestsAcc.length})</h3>
                            {requestsAcc.map((r) => (
                                <div key={r.uid} className="req">
                                    <img src={r.img || FALLBACK_AVATAR} alt="" />
                                    <span className="req-name">{r.name}</span>
                                    <button className="ok" onClick={() => decideRequest(community.id, r, true)}>Approve</button>
                                    <button className="no" onClick={() => decideRequest(community.id, r, false)}>Reject</button>
                                </div>
                            ))}
                        </RequestsCard>
                    )}

                    {/* Composer for members */}
                    {isMember && !isBanned ? (
                        <Composer communityId={community.id} community={community} />
                    ) : (
                        !isBanned && (
                            <Prompt>
                                {community.visibility === "private"
                                    ? "This community is private."
                                    : "Join this community to post."}
                            </Prompt>
                        )
                    )}

                    {/* Sort tabs */}
                    <Feed
                        communityId={community.id}
                        community={community}
                        posts={posts}
                        isAdmin={isAdmin}
                        verifiedMap={verifiedMap}
                    />

                    {/* Admin member management */}
                    {isAdmin && members.length > 0 && (
                        <MembersCard>
                            <h3>Members ({members.length})</h3>
                            {members.slice(0, 40).map((m) => {
                                const isFounder = m.uid === community.founderUid;
                                const isSelf = m.uid === uid;
                                return (
                                    <div key={m.uid} className="memb">
                                        <img src={m.img || FALLBACK_AVATAR} alt="" />
                                        <span className="m-name">
                                            {m.name}
                                            {getBadge(verifiedMap, m.uid, null, community) && (
                                                <VerifiedBadge type={getBadge(verifiedMap, m.uid, null, community)} size={14} />
                                            )}
                                            {isFounder && <em> Owner</em>}
                                            {isSelf && <em> (you)</em>}
                                        </span>
                                        <div className="m-actions">
                                            {isOwner && !isFounder && !isSelf && (
                                                <button className="tr" onClick={() => transferOwnership(community.id, m, user)}>Transfer</button>
                                            )}
                                            {!isFounder && !isSelf && (
                                                <>
                                                    <button className="rm" onClick={() => removeMember(community.id, m)}>Remove</button>
                                                    <button className="bn" onClick={() => banMember(community.id, m)}>Ban</button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </MembersCard>
                    )}
                </MainCol>

                <SideCol>
                    <AboutCard community={community} />
                    {isAdmin && bannedUids.length > 0 && (
                        <BannedCard side>
                            <h3>Banned ({bannedUids.length})</h3>
                            {bannedUids.map((bu) => (
                                <div key={bu} className="banned-row">
                                    <span className="bu">{bu === user?.uid ? "This user" : bu.slice(0, 12)}</span>
                                    <button className="ok" onClick={() => unbanMember(community.id, bu)}>Unban</button>
                                </div>
                            ))}
                        </BannedCard>
                    )}
                </SideCol>
            </Body>
        </Page>
    );
};

// ─── Composer ───
const Composer = ({ communityId, community }) => {
    const user = useSelector(selectUser);
    const [title, setTitle] = useState("");
    const [body, setBody] = useState("");
    const [image, setImage] = useState("");
    const [uploading, setUploading] = useState(false);
    const [posting, setPosting] = useState(false);

    const submit = async () => {
        if (!title.trim() || posting) return;
        setPosting(true);
        await createCommunityPost(communityId, { title: title.trim(), body: body.trim(), image }, user, community);
        setTitle("");
        setBody("");
        setImage("");
        setPosting(false);
    };

    return (
        <ComposerCard>
            <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Title"
                className="post-title"
            />
            <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Add a post..."
                rows={2}
            />
            <div className="composer-actions">
                {image && <img src={image} alt="preview" className="preview" />}
                <button
                    className="img-btn"
                    onClick={() => document.getElementById("comp-img").click()}
                >
                    {uploading ? "Uploading..." : "Image"}
                </button>
                <input
                    id="comp-img"
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={async (e) => {
                        if (!e.target.files[0]) return;
                        setUploading(true);
                        try {
                            const res = await uploadToCloudinary(e.target.files[0], "post");
                            setImage(res.url);
                        } catch {}
                        setUploading(false);
                    }}
                />
                <button className="post-btn" onClick={submit} disabled={!title.trim() || posting}>
                    Post
                </button>
            </div>
        </ComposerCard>
    );
};

// ─── Feed ───
const Feed = ({ communityId, community, posts, isAdmin, verifiedMap }) => {
    const [sort, setSort] = useState("Hot");
    const user = useSelector(selectUser);
    const now = Date.now();

    const sorted = [...posts].sort((a, b) => {
        const atA = a.createdAt?.toMillis?.() || 0;
        const atB = b.createdAt?.toMillis?.() || 0;
        const score = (p) => (p.up || 0) - (p.down || 0);
        let cmp = 0;
        switch (sort) {
            case "New":
                cmp = atB - atA;
                break;
            case "Top":
                cmp = score(b) - score(a);
                break;
            case "Most Commented":
                cmp = (b.commentCount || 0) - (a.commentCount || 0);
                break;
            case "Hot":
            default: {
                const hot = (p) => score(p) / Math.max(1, Math.pow((now - atA) / 3600000 + 2, 1.5));
                cmp = hot(b) - hot(a);
            }
        }
        if (cmp !== 0) return cmp;
        return atB - atA;
    });

    const pinned = sorted.filter((p) => p.pinned);
    const rest = sorted.filter((p) => !p.pinned);

    return (
        <>
            <SortBar>
                {SORTS.map((s) => (
                    <button key={s} className={sort === s ? "active" : ""} onClick={() => setSort(s)}>
                        {s}
                    </button>
                ))}
            </SortBar>

            {pinned.map((p) => (
                <PostCard key={p.id} communityId={communityId} community={community} post={p} user={user} isAdmin={isAdmin} verifiedMap={verifiedMap} />
            ))}

            {pinned.length === 0 && rest.length === 0 && <Empty>No posts yet in this community.</Empty>}

            {rest.map((p) => (
                <PostCard key={p.id} communityId={communityId} community={community} post={p} user={user} isAdmin={isAdmin} verifiedMap={verifiedMap} />
            ))}
        </>
    );
};

// ─── Post Card ───
const PostCard = ({ communityId, community, post, user, isAdmin, verifiedMap }) => {
    const [commentsOpen, setCommentsOpen] = useState(false);
    const [comments, setComments] = useState([]);
    const [commentText, setCommentText] = useState("");
    const [menuOpen, setMenuOpen] = useState(false);

    // edit state
    const [editing, setEditing] = useState(false);
    const [editTitle, setEditTitle] = useState(post.title);
    const [editBody, setEditBody] = useState(post.body);

    useEffect(() => {
        if (!commentsOpen) return;
        return subscribePostComments(communityId, post.id, setComments);
    }, [communityId, post.id, commentsOpen]);

    const score = (post.up || 0) - (post.down || 0);
    const isUp = user?.uid && (post.upvoters || []).includes(user.uid);
    const isDown = user?.uid && (post.downvoters || []).includes(user.uid);
    const isAuthor = user?.uid && post.authorUid === user.uid;

    const submitComment = async () => {
        if (!commentText.trim()) return;
        await addPostComment(communityId, post.id, commentText.trim(), user);
        setCommentText("");
    };

    const saveEdit = async () => {
        if (!editTitle.trim()) return;
        await editCommunityPost(communityId, post.id, { title: editTitle.trim(), body: editBody.trim(), image: post.image });
        setEditing(false);
    };

    const doDelete = async () => {
        if (!window.confirm("Delete this post?")) return;
        await deleteCommunityPost(communityId, post.id);
    };

    return (
        <PostSt>
            <VoteCol>
                <button
                    className={`vote ${isUp ? "up" : ""}`}
                    onClick={() => voteCommunityPost(communityId, post.id, user, "up")}
                >
                    {isUp ? <GoArrowUp /> : <BiUpvote />}
                </button>
                <span className={score >= 0 ? "score pos" : "score neg"}>{score}</span>
                <button
                    className={`vote ${isDown ? "down" : ""}`}
                    onClick={() => voteCommunityPost(communityId, post.id, user, "down")}
                >
                    {isDown ? <GoArrowDown /> : <BiDownvote />}
                </button>
            </VoteCol>
            <div className="post-body">
                <div className="meta-line">
                    {post.pinned && <span className="pinned-tag"><BiPin /> Pinned</span>}
                    <img src={post.authorImg || FALLBACK_AVATAR} alt="" />
                    <span className="author">
                        {post.authorName}
                        {getBadge(verifiedMap, post.authorUid, post.authorEmail, community) && (
                            <VerifiedBadge
                                type={getBadge(verifiedMap, post.authorUid, post.authorEmail, community)}
                                size={17}
                            />
                        )}
                    </span>
                    {post.editedAt && <span className="edited">(edited)</span>}
                    <span className="time">• {post.createdAt ? dayjs(post.createdAt.toMillis()).fromNow() : "just now"}</span>

                    {(isAuthor || isAdmin) && (
                        <div className="menu-wrap">
                            <button className="mm" onClick={() => setMenuOpen(!menuOpen)}>
                                <BiDotsVerticalRounded />
                            </button>
                            {menuOpen && (
                                <div className="menu">
                                    {(isAuthor || isAdmin) && <button onClick={() => { setEditing(true); setMenuOpen(false); }}><BiEdit /> Edit</button>}
                                    {isAdmin && <button onClick={() => { togglePinPost(communityId, post.id, !post.pinned); setMenuOpen(false); }}><BiPin /> {post.pinned ? "Unpin" : "Pin"}</button>}
                                    {(isAuthor || isAdmin) && <button className="danger" onClick={doDelete}><BiTrashAlt /> Delete</button>}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {editing ? (
                    <div className="edit-box">
                        <input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} />
                        <textarea value={editBody} onChange={(e) => setEditBody(e.target.value)} rows={3} />
                        <div className="edit-actions">
                            <button className="cancel" onClick={() => setEditing(false)}>Cancel</button>
                            <button onClick={saveEdit}>Save</button>
                        </div>
                    </div>
                ) : (
                    <>
                        <h3 className="ptitle">{post.title}</h3>
                        {post.body && <p className="pbody">{post.body}</p>}
                        {post.image && <img src={post.image} alt="" className="pimg" />}
                    </>
                )}

                <div className="pactions">
                    <button className="pact" onClick={() => setCommentsOpen(!commentsOpen)}>
                        <BiMessageRounded /> {post.commentCount || 0} Comments
                    </button>
                    <button className="pact" onClick={() => navigator.clipboard?.writeText(window.location.href)}>
                        <BiShareAlt /> Share
                    </button>
                </div>

                {commentsOpen && (
                    <div className="comments">
                        <div className="composer">
                            <input
                                value={commentText}
                                onChange={(e) => setCommentText(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && submitComment()}
                                placeholder="Add a comment..."
                            />
                            <button onClick={submitComment}>Post</button>
                        </div>
                        {comments.map((c) => (
                            <CommentRow key={c.id} comment={c} communityId={communityId} community={community} post={post} user={user} isAdmin={isAdmin} verifiedMap={verifiedMap} />
                        ))}
                        {!comments.length && <div className="none">No comments yet.</div>}
                    </div>
                )}
            </div>
        </PostSt>
    );
};

const CommentRow = ({ comment, communityId, community, post, user, isAdmin, verifiedMap }) => {
    const [editing, setEditing] = useState(false);
    const [text, setText] = useState(comment.text);
    const isAuthor = user?.uid && comment.authorUid === user.uid;
    const canMod = isAuthor || isAdmin;

    const save = async () => {
        if (!text.trim()) return;
        await editCommunityComment(communityId, post.id, comment.id, text.trim());
        setEditing(false);
    };

    const del = async () => {
        if (!window.confirm("Delete this comment?")) return;
        await deleteCommunityComment(communityId, post.id, comment.id);
    };

    return (
        <div className="c">
            <img src={comment.authorImg || FALLBACK_AVATAR} alt="" />
            <div className="c-body">
                <div className="c-head">
                    <span className="cname">
                        {comment.authorName}
                        {getBadge(verifiedMap, comment.authorUid, comment.authorEmail, community) && (
                            <VerifiedBadge
                                type={getBadge(verifiedMap, comment.authorUid, comment.authorEmail, community)}
                                size={15}
                            />
                        )}
                    </span>
                    {comment.editedAt && <span className="edited">(edited)</span>}
                    {canMod && (
                        <>
                            <button className="cm" onClick={() => setEditing(!editing)}><BiEdit /></button>
                            <button className="cm danger" onClick={del}><BiTrashAlt /></button>
                        </>
                    )}
                </div>
                {editing ? (
                    <>
                        <input className="c-edit" value={text} onChange={(e) => setText(e.target.value)} />
                        <div className="edit-actions">
                            <button className="cancel" onClick={() => setEditing(false)}>Cancel</button>
                            <button onClick={save}>Save</button>
                        </div>
                    </>
                ) : (
                    <p>{comment.text}</p>
                )}
            </div>
        </div>
    );
};

const AboutCard = ({ community }) => (
    <About>
        <h3>About r/{community.slug}</h3>
        <p className="desc">{community.description || "No description yet."}</p>
        <div className="stat"><span>Founded</span><span>{community.createdAt ? dayjs(community.createdAt.toMillis()).format("MMM D, YYYY") : "n/a"}</span></div>
        <div className="stat"><span>Visibility</span><span className="cap">{community.visibility}</span></div>
        <div className="stat"><span>Tags</span><span>{community.tags?.length || 0}</span></div>
        {community.rules?.length > 0 && (
            <div className="rules">
                <h4>Rules</h4>
                <ol>{community.rules.slice(0, 10).map((r, i) => <li key={i}>{r}</li>)}</ol>
            </div>
        )}
    </About>
);

export default CommunityPage;

// ─── Styles ───
const Centered = styled.div`
    min-height: 70vh;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #888;
`;

const Page = styled.div`
    min-height: 100vh;
    background-color: var(--primary-base-cream);
`;

const Header = styled.div`
    background-color: #ffffff;
    border-bottom: 1px solid rgba(201,160,99,0.2);

    .banner {
        height: 150px;
        background: ${(p) => (p.banner ? `url(${p.banner}) center/cover no-repeat` : "linear-gradient(135deg,#1DA1F2,#0d8ecf)")};
    }

    .head-inner {
        max-width: 1080px;
        width: 92%;
        margin: 0 auto;
        padding: 0.75rem 0 1rem;
        display: flex;
        gap: 14px;
        align-items: flex-end;

        .info {
            flex: 1;
            min-width: 0;

            h1 { margin: 0 0 4px; font-size: 28px; color: #0a1428; }

            .meta { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; font-size: 15px; color: #555;
                .cat { background: #e8f5fe; color: #1DA1F2; font-weight: 700; padding: 2px 12px; border-radius: 12px; font-size: 13px; }
                .dot { color: #ccc; }
            }

            .actions { display: flex; gap: 8px; margin-top: 10px;
                .join { background: linear-gradient(135deg,#1DA1F2,#0d8ecf); color: white; border: none; padding: 9px 22px; border-radius: 20px; font-size: 15px; font-weight: 700; cursor: pointer;
                    &:hover { opacity: 0.9; } &:disabled { opacity: 0.5; cursor: not-allowed; } }
                .icon-btn { display: flex; align-items: center; gap: 6px; background: transparent; border: 1px solid #e0d8cc; color: #555; padding: 8px 14px; border-radius: 20px; font-size: 14px; cursor: pointer;
                    &:hover { background: #faf7f2; } }
            }
        }
    }
`;

const AvatarC = styled.div`
    width: 88px; height: 88px;
    border-radius: 50%;
    border: 4px solid white;
    margin-top: -44px;
    background: linear-gradient(135deg,#c9a063,#e8c47d);
    overflow: hidden;
    display: flex; align-items: center; justify-content: center;
    color: white; font-size: 34px; font-weight: 700;
    img { width: 100%; height: 100%; object-fit: cover; }
`;

const Body = styled.div`
    max-width: 1080px;
    width: 92%;
    margin: 1.2rem auto;
    display: flex;
    gap: 1.2rem;
    align-items: flex-start;
`;

const MainCol = styled.div`
    flex: 1;
    min-width: 0;
`;

const SideCol = styled.div`
    width: 280px;
    flex-shrink: 0;
    position: sticky;
    top: 70px;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    @media (max-width: 900px) { display: none; }
`;

const About = styled.div`
    background: white;
    border: 1px solid rgba(201,160,99,0.25);
    border-radius: 10px;
    padding: 1rem;
    box-shadow: 0 4px 20px rgba(10,20,40,0.06);

    h3 { margin: 0 0 8px; font-size: 18px; color: #0a1428; }
    .desc { font-size: 16px; color: #555; line-height: 1.4; margin: 0 0 12px; }
    .stat { display: flex; justify-content: space-between; font-size: 15px; padding: 6px 0; border-top: 1px solid #f0ebe3; color: #333; .cap { text-transform: capitalize; } }
    .rules { margin-top: 10px; h4 { font-size: 15px; margin: 0 0 6px; } ol { margin: 0; padding-left: 18px; font-size: 14px; color: #555; } }
`;

const RequestsCard = styled.div`
    background: white;
    border: 1px solid rgba(201,160,99,0.25);
    border-radius: 10px;
    padding: 1rem;
    margin-bottom: 1rem;
    h3 { margin: 0 0 8px; font-size: 17px; }
    .req { display: flex; align-items: center; gap: 8px; padding: 6px 0; border-top: 1px solid #f0ebe3;
        img { width: 34px; height: 34px; border-radius: 50%; }
        .req-name { flex: 1; font-size: 15px; }
        button { border: none; padding: 5px 14px; border-radius: 12px; font-size: 14px; cursor: pointer; font-weight: 600; }
        .ok { background: #16a34a; color: white; }
        .no { background: #f0ebe3; color: #555; }
    }
`;

const MembersCard = styled.div`
    background: white;
    border: 1px solid rgba(201,160,99,0.25);
    border-radius: 10px;
    padding: 1rem;
    margin-bottom: 1rem;
    h3 { margin: 0 0 8px; font-size: 17px; }
    .memb { display: flex; align-items: center; gap: 9px; padding: 7px 0; border-top: 1px solid #f0ebe3;
        img { width: 34px; height: 34px; border-radius: 50%; }
        .m-name { flex: 1; font-size: 15px; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; em { font-style: normal; color: #c9a063; font-size: 13px; } }
        .m-actions { display: flex; gap: 5px; flex-shrink: 0; }
        button { border: none; padding: 4px 11px; border-radius: 10px; font-size: 13px; cursor: pointer; font-weight: 600; }
        .tr { background: #1DA1F2; color: white; }
        .rm { background: #f0ebe3; color: #555; }
        .bn { background: #ffebee; color: #d32f2f; }
    }
`;

const BannedCard = styled.div`
    background: ${(p) => (p.side ? "white" : "#ffebee")};
    color: #b3261e;
    border: 1px solid rgba(211,47,47,0.2);
    border-radius: 10px;
    padding: 1rem;
    margin-bottom: 1rem;
    font-size: 16px;
    text-align: ${(p) => (p.side ? "left" : "center")};
    h3 { margin: 0 0 8px; font-size: 17px; }
    .banned-row { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 5px 0; border-top: 1px solid #f5ddd8;
        .bu { font-size: 14px; color: #b3261e; word-break: break-all; }
        .ok { border: none; background: #16a34a; color: white; padding: 4px 12px; border-radius: 10px; font-size: 13px; font-weight: 600; cursor: pointer; }
    }
`;

const Prompt = styled.div`
    background: white;
    border: 1px solid rgba(201,160,99,0.25);
    border-radius: 10px;
    padding: 1.2rem;
    text-align: center;
    color: #888;
    font-size: 17px;
    margin-bottom: 1rem;
`;

const ComposerCard = styled.div`
    background: white;
    border: 1px solid rgba(201,160,99,0.25);
    border-radius: 10px;
    padding: 1.2rem;
    margin-bottom: 1rem;
    display: flex;
    flex-direction: column;
    gap: 12px;

    .post-title { border: none; border-bottom: 1px solid #e0d8cc; padding: 8px 2px; font-size: 20px; font-weight: 600; outline: none; font-family: inherit; }
    textarea { border: none; outline: none; resize: vertical; font-size: 17px; font-family: inherit; }
    .composer-actions { display: flex; align-items: center; gap: 10px; }
    .preview { width: 110px; height: 70px; object-fit: cover; border-radius: 6px; }
    .img-btn { background: #f0ebe3; border: none; padding: 9px 18px; border-radius: 6px; cursor: pointer; font-size: 15px; }
    .post-btn { margin-left: auto; background: linear-gradient(135deg,#1DA1F2,#0d8ecf); color: white; border: none; padding: 9px 24px; border-radius: 18px; font-weight: 700; font-size: 15px; cursor: pointer; &:disabled { opacity: 0.5; cursor: not-allowed; } }
`;

const SortBar = styled.div`
    display: flex;
    gap: 8px;
    margin-bottom: 1rem;
    button { background: transparent; border: none; padding: 8px 14px; border-radius: 18px; font-size: 16px; font-weight: 600; color: #666; cursor: pointer;
        &.active { background: #1DA1F2; color: white; }
        &:hover:not(.active) { background: #eee2ce; }
    }
`;

const Empty = styled.div`
    background: white;
    border: 1px solid rgba(201,160,99,0.25);
    border-radius: 10px;
    padding: 2.2rem;
    text-align: center;
    color: #999;
    font-size: 16px;
`;

const PostSt = styled.div`
    background: white;
    border: 1px solid rgba(201,160,99,0.25);
    border-radius: 10px;
    display: flex;
    margin-bottom: 0.9rem;
    overflow: hidden;
    box-shadow: 0 2px 10px rgba(10,20,40,0.04);

    .vote { background: transparent; border: none; color: #878a8c; font-size: 24px; cursor: pointer; padding: 5px; &.up { color: #1DA1F2; } &.down { color: #e53935; } }
    .score { font-size: 17px; font-weight: 700; color: #1a1a1b; &.pos { color: #1DA1F2; } &.neg { color: #e53935; } }

    .post-body { flex: 1; padding: 1.1rem 1.3rem; min-width: 0;
        .meta-line { display: flex; align-items: center; gap: 9px; margin-bottom: 8px; flex-wrap: wrap;
            img { width: 34px; height: 34px; border-radius: 50%; }
            .pinned-tag { display: inline-flex; align-items: center; gap: 3px; background: #fff3cd; color: #8a6d1a; font-size: 13px; font-weight: 700; padding: 2px 10px; border-radius: 10px; }
            .author { font-size: 17px; font-weight: 700; color: #0a1428; }
            .time { font-size: 14px; color: #999; }
            .edited { font-size: 13px; color: #bbb; font-style: italic; }
            .menu-wrap { margin-left: auto; position: relative;
                .mm { background: transparent; border: none; color: #999; font-size: 22px; cursor: pointer; padding: 2px 4px; &:hover { color: #333; } }
                .menu { position: absolute; right: 0; top: 24px; background: white; border: 1px solid #e0d8cc; border-radius: 8px; box-shadow: 0 8px 25px rgba(0,0,0,0.12); z-index: 20; min-width: 150px; padding: 4px;
                    button { display: flex; align-items: center; gap: 8px; width: 100%; background: transparent; border: none; padding: 9px 12px; font-size: 15px; cursor: pointer; color: #333; border-radius: 6px;
                        &:hover { background: #f0ebe3; }
                        &.danger { color: #d32f2f; &:hover { background: #ffebee; } }
                    }
                }
            }
        }
        .ptitle { font-size: 24px; font-weight: 700; color: #0a1428; margin: 0 0 6px; line-height: 1.3; }
        .pbody { font-size: 18px; color: #333; margin: 0 0 10px; white-space: pre-wrap; line-height: 1.45; }
        .pimg { width: 100%; max-height: 420px; object-fit: cover; border-radius: 8px; margin-bottom: 10px; }

        .edit-box { display: flex; flex-direction: column; gap: 10px; margin: 8px 0;
            input, textarea { border: 1px solid #e0d8cc; border-radius: 6px; padding: 10px; font-size: 16px; font-family: inherit; }
            .edit-actions { display: flex; gap: 8px; justify-content: flex-end;
                button { border: none; border-radius: 16px; padding: 8px 18px; font-size: 15px; font-weight: 600; cursor: pointer; background: #1DA1F2; color: white; &.cancel { background: #f0ebe3; color: #555; } }
            }
        }

        .pactions { display: flex; gap: 12px; margin-top: 8px;
            .pact { display: flex; align-items: center; gap: 7px; background: transparent; border: none; color: #7c7c7c; font-size: 16px; cursor: pointer; padding: 6px 12px; border-radius: 16px;
                &:hover { background: #f0ebe3; } }
        }

        .comments { margin-top: 12px; border-top: 1px solid #f0ebe3; padding-top: 12px;
            .composer { display: flex; gap: 8px; margin-bottom: 10px;
                input { flex: 1; border: 1px solid #e0d8cc; border-radius: 18px; padding: 9px 14px; font-size: 16px; outline: none; }
                button { background: #1DA1F2; color: white; border: none; border-radius: 16px; padding: 8px 18px; font-weight: 600; cursor: pointer; font-size: 15px; }
            }
            .c { display: flex; gap: 9px; padding: 8px 0;
                > img { width: 32px; height: 32px; border-radius: 50%; }
                .c-body { flex: 1; min-width: 0;
                    .c-head { display: flex; align-items: center; gap: 7px;
                        .cname { font-size: 16px; font-weight: 700; color: #0a1428; }
                        .edited { font-size: 13px; color: #bbb; font-style: italic; }
                        .cm { background: transparent; border: none; color: #999; cursor: pointer; font-size: 15px; padding: 0 2px; &:hover { color: #333; } &.danger:hover { color: #d32f2f; } }
                    }
                    p { margin: 3px 0 0; font-size: 16px; color: #333; }
                    .c-edit { border: 1px solid #e0d8cc; border-radius: 6px; padding: 8px; font-size: 15px; width: 100%; box-sizing: border-box; }
                    .edit-actions { display: flex; gap: 6px; margin-top: 5px; justify-content: flex-end;
                        button { border: none; border-radius: 12px; padding: 5px 14px; font-size: 14px; font-weight: 600; cursor: pointer; background: #1DA1F2; color: white; &.cancel { background: #f0ebe3; color: #555; } }
                    }
                }
            }
            .none { color: #999; font-size: 15px; }
        }
    }
`;

const VoteCol = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 3px;
    padding: 10px 8px;
    background: #fafaf9;
    width: 56px;
    flex-shrink: 0;
`;